import React, { useState, useEffect } from "react";
import "./ContactUs.scss";

import emailjs from "@emailjs/browser";

import {
  useToast,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuItem,
  Textarea,
} from "@chakra-ui/react";

import { ChevronDownIcon, EmailIcon } from "@chakra-ui/icons";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/high-res.css";

const REACT_OUT_TEXT = "Reach out to Workoza.";

const REACT_OUT_SUB_TEXT =
  "Don’t be shy! We welcome your comments, questions, and feedback. If you’re ready for your next assignment or career step, you can also search our open jobs. To connect with a local recruiter or sales person, fill out the form provided below.";

const PRIVACY_INFO_TEXT =
  "Workoza uses the contact information you provide to us to contact you about our services. You may unsubscribe from these communications at any time.";

const PLACEHOLDER_TEXT = {
  NAME: "Enter your name",
  PHONE_NUMBER: "Enter your phone number",
  EMAIL: "Enter your email",
  COUNTRY: "Enter your country",
};

const SUBJECT_TEXT = ["Inquiry", "Consulting", "Job opportunities"];

const ERROR_TEXT = {
  NAME: "name is required.",
  PHONE_NUMBER: "phone number is not valid.",
  EMAIL: "email is not valid.",
  COUNTRY: "country is required.",
};

const defaultStyles = {
  containerStyle: {
    width: "100%",
  },
  inputStyle: {
    width: "100%",
    height: "40px",
    backgroundColor: "inherit",
    fontSize: "1rem",
    color: "#717171",
    border: "2px solid #edf0f0",
    borderRadius: "8px",
  },
  buttonStyle: {
    borderTop: "2px solid #edf0f0",
    borderBottom: "2px solid #edf0f0",
    borderRadius: 0,
    borderRight: "none",
    borderLeft: "none",
    marginLeft: "8px",
    width: "48px",
    backgroundColor: "#ffffff",
  },
  focusStyle: {
    border: "2px solid #2b6cb1",
  },
  buttonFocusStyle: {
    borderTop: "2px solid #2b6cb1",
    borderBottom: "2px solid #2b6cb1",
  },
  errorStyle: {
    border: "2px solid #e43e3f",
  },
  buttonErrorStyle: {
    borderTop: "2px solid #e43e3f",
    borderBottom: "2px solid #e43e3f",
  },
};

const ContactUs = () => {
  const [name, setName] = useState("");
  const [isNameNotValid, setIsNameNotValid] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState({});
  const [isPhoneNumberFocused, setIsPhoneNumberFocused] = useState(false);
  const [isPhoneNumberNotValid, setIsPhoneNumberNotValid] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailNotValid, setIsEmailNotValid] = useState(true);
  const [country, setCountry] = useState("");
  const [isCountryNotValid, setIsCountryNotValid] = useState(false);
  const [subject, setSubject] = useState("Inquiry");
  const [inquiry, setInquiry] = useState("");

  const toast = useToast();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const renderContactusTopbar = () => {
    return (
      <div className="contactusTopBarContainer">
        <div className="contactusTopBarMainText">{REACT_OUT_TEXT}</div>
        <div className="contactusTopBarMainSubText">{REACT_OUT_SUB_TEXT}</div>
      </div>
    );
  };

  const renderContactUsForm = () => {
    const handleNameChange = (e) => {
      setName(e?.target?.value);
    };
    const handlePhoneNumberChange = (phone, other) => {
      setPhoneNumber({
        phone,
        other,
      });
    };
    const handleEmailChange = (e) => {
      setEmail(e?.target?.value);
    };
    const handleCountryChange = (e) => {
      setCountry(e?.target?.value);
    };
    const handleSubjectChange = (e) => {
      setSubject(e?.target?.value);
    };
    const handleInquiryChange = (e) => {
      setInquiry(e?.target?.value);
    };

    const handleSubmitDetails = async () => {
      const checkIsNameNotValid = name?.length <= 0;
      const checkPhoneNumberNotValid =
        phoneNumber?.other?.name === "India"
          ? phoneNumber?.phone?.length !== 12
          : phoneNumber?.phone?.length !== 11;
      const checkEmailNotValid = String(email)
        .toLowerCase()
        .match(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
      const checkCountryNotValid = country?.length <= 0;

      setIsNameNotValid(checkIsNameNotValid);
      setIsPhoneNumberNotValid(checkPhoneNumberNotValid);
      setIsEmailNotValid(checkEmailNotValid);
      setIsCountryNotValid(checkCountryNotValid);

      if (
        !checkIsNameNotValid &&
        !checkPhoneNumberNotValid &&
        checkEmailNotValid &&
        !checkCountryNotValid
      ) {
        const formattedPhoneNumber =
          String(phoneNumber?.other?.name) +
          " " +
          `+${phoneNumber?.other?.dialCode}` +
          " " +
          String(phoneNumber?.phone);
        const formData = {
          name,
          phoneNumber: formattedPhoneNumber,
          email,
          country,
          subject,
          inquiry,
          from_email: email,
        };
        await emailjs
          .send(
            process.env.REACT_APP_EMAIL_SERVICE_ID,
            process.env.REACT_APP_EMAIL_TEMPLATE_ID,
            formData,
            process.env.REACT_APP_EMAIL_PUBLIC_KEY
          )
          .then(
            (result) => {
              console.log("Email sent successfully...🚀");
              setName("");
              setIsNameNotValid(false);
              setPhoneNumber({});
              setIsPhoneNumberNotValid(false);
              setIsPhoneNumberFocused(false);
              setEmail("");
              setIsEmailNotValid(true);
              setCountry("");
              setIsCountryNotValid(false);
              setSubject("Inquiry");
              setInquiry("");
            },
            (error) => {
              console.log("Something went wrong!!!");
            }
          );
        toast({
          title: "Your details sent successfully.",
          status: "success",
          position: "bottom-left",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const mergedErrInputStyle = {
      ...defaultStyles.inputStyle,
      ...defaultStyles.errorStyle,
    };
    const mergedFocusInputStyle = {
      ...defaultStyles.inputStyle,
      ...defaultStyles.focusStyle,
    };

    const mergedErrButtonStyle = {
      ...defaultStyles.buttonStyle,
      ...defaultStyles.buttonErrorStyle,
    };
    const mergedFocusButtonStyle = {
      ...defaultStyles.buttonStyle,
      ...defaultStyles.buttonFocusStyle,
    };

    const handleFocus = () => {
      setIsPhoneNumberFocused(true);
    };

    const handleBlur = () => {
      setIsPhoneNumberFocused(false);
    };

    const phoneNumberDigits = phoneNumber?.phone;

    return (
      <div className="contactUsFormContainer">
        <div className="contactUsNameContainer">
          <FormControl isRequired isInvalid={isNameNotValid}>
            <FormLabel>Name</FormLabel>
            <Input
              type="email"
              placeholder={PLACEHOLDER_TEXT.NAME}
              value={name}
              onChange={handleNameChange}
            />
            <FormErrorMessage>{ERROR_TEXT.NAME}</FormErrorMessage>
          </FormControl>
        </div>
        <div className="contactusPhoneEmailContainer">
          <div className="contactUsPhoneContainer">
            <FormControl isRequired isInvalid={isPhoneNumberNotValid}>
              <FormLabel>Phone number</FormLabel>
              <PhoneInput
                country={"in"}
                placeholder={PLACEHOLDER_TEXT.PHONE_NUMBER}
                onlyCountries={["in", "ca", "us"]}
                onFocus={handleFocus}
                onBlur={handleBlur}
                inputStyle={
                  isPhoneNumberNotValid
                    ? mergedErrInputStyle
                    : isPhoneNumberFocused
                    ? mergedFocusInputStyle
                    : defaultStyles.inputStyle
                }
                buttonStyle={
                  isPhoneNumberNotValid
                    ? mergedErrButtonStyle
                    : isPhoneNumberFocused
                    ? mergedFocusButtonStyle
                    : defaultStyles.buttonStyle
                }
                containerClass={defaultStyles.containerStyle}
                value={phoneNumberDigits ? phoneNumberDigits : ""}
                onChange={handlePhoneNumberChange}
              />
              <FormErrorMessage>{ERROR_TEXT.PHONE_NUMBER}</FormErrorMessage>
            </FormControl>
          </div>
          <div className="contactUsEmailContainer">
            <FormControl isRequired isInvalid={!isEmailNotValid}>
              <FormLabel>Email</FormLabel>
              <Input
                type="text"
                placeholder={PLACEHOLDER_TEXT.EMAIL}
                value={email}
                onChange={handleEmailChange}
              />
              <FormErrorMessage>{ERROR_TEXT.EMAIL}</FormErrorMessage>
            </FormControl>
          </div>
        </div>
        <div className="contactusCountrySubjectContainer">
          <div className="contactUsCountryContainer">
            <FormControl isRequired isInvalid={isCountryNotValid}>
              <FormLabel>Country</FormLabel>
              <Input
                type="text"
                placeholder={PLACEHOLDER_TEXT.COUNTRY}
                value={country}
                onChange={handleCountryChange}
              />
              <FormErrorMessage>{ERROR_TEXT.COUNTRY}</FormErrorMessage>
            </FormControl>
          </div>
          <div className="contactUsSubjectContainer">
            <FormLabel>Subject</FormLabel>
            <Menu placement="bottom" onChange={handleSubjectChange}>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                width="100%"
              >
                {subject}
              </MenuButton>
              <MenuList>
                {SUBJECT_TEXT.map((subject, index) => {
                  return (
                    <MenuItem
                      value={subject}
                      onClick={handleSubjectChange}
                      key={index}
                    >
                      {subject}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
          </div>
        </div>
        <div className="contactusInquiryContainer">
          <FormControl>
            <FormLabel>Inquiry details</FormLabel>
            <Textarea
              value={inquiry}
              onChange={handleInquiryChange}
              size="sm"
              resize={"none"}
            />
          </FormControl>
        </div>
        <div className="contactusPrivacyInfo">{PRIVACY_INFO_TEXT}</div>
        <div className="contactusSubmitButtonContainer">
          <Button
            rightIcon={<EmailIcon />}
            size="md"
            variant="solid"
            color="#ffffff"
            background="#d33853"
            _hover={{ background: "#af273e", color: "#f9f9f9" }}
            onClick={handleSubmitDetails}
          >
            Submit
          </Button>
        </div>
      </div>
    );
  };

  const renderContactusFeed = () => {
    return (
      <div className="contactusWrapper">
        <div className="contactusFeedImgContainer">
          <img
            src="/contactus_banner.jpeg"
            alt="no banner"
            className="contactusBannerImage"
          />
        </div>
        <div className="contactusFormContainer">
          <div className="contactusContactusText">Contact Us</div>
          <div className="contactusContactusSubText">
            Please fill the form below and a Workoza Account Manager will
            contact you.
          </div>
          {renderContactUsForm()}
        </div>
      </div>
    );
  };

  return (
    <div className="contactusContainer">
      {renderContactusTopbar()}
      {renderContactusFeed()}
    </div>
  );
};

export default ContactUs;
